import { useInCreateDialogSections } from 'components/create-dialog/CreateDialogRequired';
import { useDraftSaveMode } from 'components/drafts/DraftSaveMode';
import { Add, Remove } from 'components/icons';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { InputGroupButton } from 'components/ui/input-group';
import { addDays, format, isValid, subDays } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useMemo, useRef } from 'react';
import { Controller, FieldValues, Path, SetFieldValue, useWatch } from 'react-hook-form';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';
import { DatePickerField } from '../common/DatePickerField';
import ReadonlyField from '../rhf-other/ReadonlyField';
import ChangeReasonInput from './ChangeReasonInput';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useIsNewDesignInput, useOwnPlaceholder } from './newDesignInput';
import { useMemoizedRules } from './useMemoizedRules';

interface DateInputProps<T extends FieldValues> extends Omit<CommonInputProps<T>, 'onChange'> {
    readonly?: boolean;
    fullWidth?: boolean;
    maxDate?: Date;
    minDate?: Date;
    hasErrors?: boolean;
    clearable?: boolean;
    useIncrementDecrementButtons?: boolean;
    setValue?: SetFieldValue<T>;
    scopeCompanyId?: number | null;
    // `month` makes the popup a month grid; a pick stores the first of that month.
    granularity?: 'day' | 'month';
    // DateInput emits a `Date | null` at runtime (see the picker `onChange` below); CommonInputProps's
    // path-typed `onChange` doesn't reflect that, so we override.
    onChange?: (value: Date | null) => void;
}

const DateInput = <T extends FieldValues>({
    disabled,
    name,
    placeholder,
    shouldTranslatePlaceholder = true,
    control,
    required,
    label,
    shouldTranslateLabel,
    rightLabelComponent,
    wrapperClassName,
    validate,
    fullWidth,
    readonly = false,
    maxDate = undefined,
    minDate = undefined,
    onChange,
    hasErrors,
    noFullWidthInputWrapper,
    tooltip,
    gap,
    withChangeReason = false,
    clearable = true,
    useIncrementDecrementButtons = false,
    setValue,
    scopeCompanyId,
    granularity = 'day',
}: DateInputProps<T>) => {
    const isNewDesign = useIsNewDesignInput();
    const ownPlaceholder = useOwnPlaceholder(placeholder, label, shouldTranslatePlaceholder);
    const reasonFieldName = `changeReasons.${name}` as Path<T>;
    const effectiveFormat = useEffectiveDateFormat(scopeCompanyId);
    // What the field accepts, as its format error spells it: "DD.MM.YYYY".
    const formatHint = effectiveFormat.toUpperCase();
    // Text that is no date is stored as '', and so is a field a form starts without one: the stored value
    // cannot tell them apart, so whether the text in the field reads as a date is remembered here. The picker
    // says so on every edit of the text, and its `onChange` covers a day picked, a clear and a completed entry.
    const holdsUnreadText = useRef(false);
    // The required rule reports that '' as a missing value. The new design checks both cases itself, and tells
    // the reader which of the two it is.
    const sharedRules = useMemoizedRules({ required: required && !isNewDesign, validate });
    // The new design's own check that a date is there is off while a draft is saved, as the required rule is.
    const drafting = useDraftSaveMode();
    const isRequired = !!required && !drafting;
    const rules = useMemo(
        () =>
            isNewDesign
                ? {
                      ...sharedRules,
                      validate: {
                          date: (value: unknown) =>
                              value === '' && holdsUnreadText.current
                                  ? t('validation.date-format', { format: formatHint })
                                  : isRequired && (value == null || value === '')
                                    ? t('validation.date-required')
                                    : true,
                          ...sharedRules.validate,
                      },
                  }
                : sharedRules,
        [isNewDesign, sharedRules, formatHint, isRequired]
    );
    const fillsCell = useInCreateDialogSections();
    const inSection = useInRecordSection();

    const currentValue = useWatch({ control, name });

    const handleIncrement = () => {
        if (!setValue) return;
        const currentDate = currentValue ? dateStringToDateObject(currentValue) : new Date();
        if (!currentDate || isNaN(currentDate.getTime())) return;
        const newDate = addDays(currentDate, 1);
        if (maxDate && newDate > maxDate) return;
        const formatted = formatDateStringInput(newDate);
        setValue(name, formatted, { shouldValidate: true, shouldDirty: true });
        if (onChange) {
            onChange(newDate);
        }
    };

    const handleDecrement = () => {
        if (!setValue) return;
        const currentDate = currentValue ? dateStringToDateObject(currentValue) : new Date();
        if (!currentDate || isNaN(currentDate.getTime())) return;
        const newDate = subDays(currentDate, 1);
        if (minDate && newDate < minDate) return;
        const formatted = formatDateStringInput(newDate);
        setValue(name, formatted, { shouldValidate: true, shouldDirty: true });
        if (onChange) {
            onChange(newDate);
        }
    };

    const showButtons = useIncrementDecrementButtons && setValue && !disabled && !readonly;

    return (
        <>
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field: { onChange: innerOnChange, value, ...field }, fieldState: { error } }) => {
                    const isEmpty = value === undefined || value === null || value === '';
                    return (
                        <InputWrapper
                            name={name}
                            label={label}
                            shouldTranslateLabel={shouldTranslateLabel}
                            required={required}
                            isEmpty={isEmpty}
                            readonly={!!readonly}
                            rightLabelComponent={rightLabelComponent}
                            hasErrors={!!error || hasErrors == true}
                            error={readonly ? undefined : error}
                            className={wrapperClassName}
                            noFullWidthInputWrapper={noFullWidthInputWrapper}
                            tooltip={tooltip}
                            gap={gap}
                        >
                            {readonly ? (
                                <ReadonlyField
                                    value={
                                        typeof value === 'string' && value
                                            ? format(dateStringToDateObject(value), effectiveFormat)
                                            : value
                                    }
                                />
                            ) : (
                                <DatePickerField
                                    mode={granularity === 'month' ? 'month' : 'date'}
                                    id={name}
                                    inputRef={field.ref}
                                    // A stored '' reads as an invalid date, the entry still being typed, so the field
                                    // keeps its text and does not emit again on every keystroke.
                                    value={value == null ? null : dateStringToDateObject(value)}
                                    onUnreadableText={(isUnreadable) => {
                                        holdsUnreadText.current = isUnreadable;
                                    }}
                                    onChange={(val) => {
                                        holdsUnreadText.current = !!val && !isValid(val);
                                        if (val) {
                                            innerOnChange(formatDateStringInput(val));
                                        } else {
                                            innerOnChange(null);
                                        }
                                        // An entry still being typed is stored as '' but not announced: callers format
                                        // the date they get, or save the form on it.
                                        if (onChange && (!val || isValid(val))) {
                                            onChange(val);
                                        }
                                    }}
                                    format={effectiveFormat}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    disabled={disabled}
                                    invalid={error ? true : undefined}
                                    clearable={clearable}
                                    actions={clearable ? undefined : ['today', 'accept']}
                                    // With none of its own, the new design leaves the picker's: the pattern it accepts.
                                    placeholder={ownPlaceholder ?? (isNewDesign ? undefined : t('select'))}
                                    // Half the row by default, but never narrower than a 10-character date beside
                                    // its clear and calendar buttons, nor wider than the row. In the create dialog
                                    // a field is one cell of a two-column grid already, which the date fills. In a
                                    // record section it fills its cell, as its read value's box does.
                                    className={
                                        fullWidth || fillsCell || inSection
                                            ? undefined
                                            : 'w-1/2 min-w-[min(11rem,100%)]'
                                    }
                                    endAddon={
                                        showButtons ? (
                                            <>
                                                <InputGroupButton
                                                    size="icon-xs"
                                                    onClick={handleDecrement}
                                                    aria-label={t('decrement-date')}
                                                >
                                                    <Remove fontSize="small" />
                                                </InputGroupButton>
                                                <InputGroupButton
                                                    size="icon-xs"
                                                    onClick={handleIncrement}
                                                    aria-label={t('increment-date')}
                                                >
                                                    <Add fontSize="small" />
                                                </InputGroupButton>
                                            </>
                                        ) : null
                                    }
                                />
                            )}
                        </InputWrapper>
                    );
                }}
            />
            {withChangeReason ? <ChangeReasonInput control={control} name={reasonFieldName} required={true} /> : null}
        </>
    );
};

export default DateInput;
